import type { Tool, Phase } from "@logic/lib/design"
import {
  atom,
  atomFamily,
  DefaultValue,
  selector,
  useRecoilTransaction_UNSTABLE,
} from "recoil"

import { designRequestState } from "~/containers/Proposal/state/design-request/use-design-request"
import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { proposalLocalState } from "~/containers/Proposal/state/proposal/proposal-state"
import { currentUserState } from "~/containers/Proposal/state/user/user-state"
import { Permission } from "~/graphql/client"
import type { Interlingual } from "~/i18n/types"

import type { TransactionOperation } from "../elements/transactions/transactors"
import { readonlyTransactors } from "../elements/transactions/transactors"
import { phaseState } from "../phase"

export type { Tool }

export type MouseControlsPerTool<T = MouseEvent> = Partial<
  Record<Tool, (e: T) => void>
>

export const TOOLS_PER_PHASE: Record<Phase, (Tool | Tool[])[]> = {
  "Image Selection": [`Pan`],
  "Outline": [`Select`, `Draw`, `Pan`, `Erase`, `Move`, `Ruler`],
  "Edge Designation": [`Pan`, `EdgeDesignator`, `Move`, `Ruler`],
  "Roof Attributes": [`Pan`, `Select`, `Move`, `Ruler`],
  "Panel Placement": [
    `Select`,
    `Pan`,
    `PanelCreatorPortrait`,
    `PanelCreatorLandscape`,
    `PanelEraser`,
    `Move`,
    `Ruler`,
  ],
  "Panel Activation": [`Pan`, `PanelActivator`, `Move`, `Ruler`],
}

export const FAVORITE_TOOL_PER_PHASE: Record<Phase, Tool> = {
  "Image Selection": `Pan`,
  "Outline": `Draw`,
  "Edge Designation": `EdgeDesignator`,
  "Roof Attributes": `Select`,
  "Panel Placement": `PanelCreatorPortrait`,
  "Panel Activation": `PanelActivator`,
}

export const TOOL_LABELS: Record<Tool, Interlingual> = {
  Select: `select`,
  Draw: `draw`,
  Pan: `pan`,
  Erase: `erase`,
  PanelActivator: `panelActivator`,
  PanelCreatorPortrait: `panelCreatorPortrait`,
  PanelCreatorLandscape: `panelCreatorLandscape`,
  PanelEraser: `panelEraser`,
  GroundMount: `groundMount`,
  Trees: `trees`,
  EdgeDesignator: `edgeDesignator`,
  Move: `move`,
  Ruler: `ruler`,
}

export const TOOL_DESC: Record<Tool, Interlingual> = {
  Select: `selectDesc`,
  Draw: `drawToolDesc`,
  Pan: `panDesc`,
  Erase: `eraseToolDesc`,
  PanelActivator: `panelActivatorDesc`,
  PanelCreatorPortrait: `panelCreatorPortraitDesc`,
  PanelCreatorLandscape: `panelCreatorPortraitDesc`,
  PanelEraser: `panelEraserDesc`,
  GroundMount: `groundMountToolDesc`,
  Trees: `treeToolDesc`,
  EdgeDesignator: `edgeDesignatorDesc`,
  Move: `moveDesc`,
  Ruler: `rulerDesc`,
}

export const TOOL_HOTKEYS: Record<Tool, string> = {
  Select: `s`,
  Draw: `d`,
  Pan: `a`,
  Erase: `e`,
  PanelActivator: `w`,
  PanelCreatorPortrait: `c`,
  PanelCreatorLandscape: `v`,
  PanelEraser: `x`,
  GroundMount: `g`,
  Trees: `t`,
  EdgeDesignator: `b`,
  Move: `m`,
  Ruler: `r`,
}

export const findToolTrayLastUsedState = atomFamily<number, Tool[]>({
  key: `toolTrayLastUsed`,
  default: 0,
})

export const openToolTrayState = atom<Tool[] | null>({
  key: `openToolTray`,
  default: null,
})

export const enabledToolsState = selector<(Tool | Tool[])[]>({
  key: `enabledTools`,
  get: ({ get }) => TOOLS_PER_PHASE[get(phaseState)],
})

export const getTool: TransactionOperation<undefined, Tool> = ({ get }) => {
  const { tool } = get(designLocalState)
  const { id, permissions } = get(currentUserState)

  // only assigned designer can use tools in service queue
  const { designUser } = get(designRequestState) ?? {}
  const { pathname } = window.location
  if (pathname.includes(`service-queue`)) {
    if (id === designUser?.userId) return tool

    return `Select`
  }

  const anyoneTools: Tool[] = [`Select`, `Pan`]
  if (anyoneTools.includes(tool)) return tool

  // sellers and mentors without design permissions tools
  const anySellerOrMentorTools: Tool[] = [`PanelActivator`]

  const { sellerId, mentorId } = get(proposalLocalState)
  const isSeller = id === sellerId
  const isMentor = id === mentorId
  const isDesigner = permissions?.includes(Permission.vision_designer_user)
  const isAdmin = permissions?.includes(Permission.vision_admin)
  if (anySellerOrMentorTools.includes(tool)) {
    if (isSeller || isMentor || isAdmin || isDesigner) return tool

    return `Select`
  }

  // all other tools are for admin and designers
  if (isDesigner || isAdmin) return tool

  return `Select`
}

export const setTool: TransactionOperation<Tool> = ({ set }, tool) =>
  set(designLocalState, (current) => ({ ...current, tool }))

export const trySetTool: TransactionOperation<DefaultValue | Tool> = (
  transactors,
  newValue,
) => {
  const { get, set } = transactors
  const phase = get(phaseState)
  const enabledTools = TOOLS_PER_PHASE[phase]
  if (newValue instanceof DefaultValue) {
    setTool(transactors, FAVORITE_TOOL_PER_PHASE[phase])
  } else if (
    enabledTools.includes(newValue) ||
    enabledTools.find((t) => t instanceof Array && t.includes(newValue))
  ) {
    setTool(transactors, newValue)
    let lastUsedIndex = 0
    const toolTray = enabledTools.find((item) => {
      if (item instanceof Array) {
        lastUsedIndex = item.indexOf(newValue)

        return lastUsedIndex !== -1
      }
    })
    if (toolTray instanceof Array) {
      set(findToolTrayLastUsedState(toolTray), lastUsedIndex)
    }
  }
}

export const toolState = selector<Tool>({
  key: `tool`,
  get: ({ get }) => getTool(readonlyTransactors(get)),
  set: (transactors, newValue) => trySetTool(transactors, newValue),
})

const toolSwappedState = atom<Tool>({
  key: `toolSwapped`,
  default: `Select`,
})

export const swapTool: TransactionOperation<DefaultValue | Tool> = (
  transactors,
  newValue,
) => {
  const { get, set } = transactors
  if (newValue instanceof DefaultValue) {
    trySetTool(transactors, get(toolSwappedState))
  } else {
    set(toolSwappedState, getTool(transactors))
    trySetTool(transactors, newValue)
  }
}

const trySwapToPanelEraser: TransactionOperation = (transactors) => {
  const tool = getTool(transactors)
  if (tool.includes(`PanelCreator`)) {
    swapTool(transactors, `PanelEraser`)
  } else if (tool === `PanelEraser`) {
    swapTool(transactors, new DefaultValue())
  }
}

export const useTrySwapToPanelEraser = (): (() => void) =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => trySwapToPanelEraser(transactors),
  )

export const temporaryToolState = selector<Tool>({
  key: `holdTool`,
  get: ({ get }) => get(toolSwappedState),
  set: swapTool,
})
